import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Badge,
  Spacer,
  Heading,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { AiOutlineArrowLeft, AiOutlinePlus, AiOutlineDelete, AiOutlineHistory } from "react-icons/ai";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

import countriesService from "../../services/countries.service";
import rulesService from "../../services/rules.service";
import AuthService from "../../services/auth.service";
import ConfirmDialog from "./confirmDialog";
import RuleAuditModal from "./RuleAuditModal";

const DEFAULT_KEY = "default";
const DEFAULT_ENTRY = { code: DEFAULT_KEY, name: "Default" };

// rules object (keyed by column) <-> editable grid rows
const rulesToRows = (rules) =>
  Object.entries(rules).map(([column, def]) => ({
    column,
    operator: def.operator || "",
    threshold: def.threshold === undefined || def.threshold === null ? "" : String(def.threshold),
    thresholdForNextCase:
      def.thresholdForNextCase === true ? "Weekly" : def.thresholdForNextCase === false ? "Daily" : "",
    thresholdCondition: def.thresholdCondition || "",
    range: def.range === undefined || def.range === null ? "" : String(def.range),
    color: def.color || "",
    datatype: def.datatype || "",
    symbol: def.symbol || "",
    isExactValueRequired: def.isExactValueRequired ? "Yes" : "",
  }));

const rowsToRules = (rows) => {
  const rules = {};
  rows.forEach((r) => {
    const def = {};
    const op = r.operator || null;
    def.operator = op;
    if (op) {
      const n = Number(r.threshold);
      def.threshold = r.threshold !== "" && !isNaN(n) ? n : r.threshold;
    }
    if (r.color) def.color = r.color;
    if (r.datatype) def.datatype = r.datatype;
    if (r.symbol) def.symbol = r.symbol;
    if (r.thresholdForNextCase === "Weekly") def.thresholdForNextCase = true;
    else if (r.thresholdForNextCase === "Daily") def.thresholdForNextCase = false;
    if (r.thresholdCondition) def.thresholdCondition = r.thresholdCondition;
    if (r.range !== "" && !isNaN(Number(r.range))) def.range = Number(r.range);
    if (r.isExactValueRequired === "Yes") def.isExactValueRequired = true;
    rules[r.column] = def;
  });
  return rules;
};

const columnDefs = [
  {
    headerName: "",
    field: "__remove",
    cellRenderer: "removeBtn",
    pinned: "left",
    width: 48,
    editable: false,
    sortable: false,
    filter: false,
    suppressMenu: true,
    headerTooltip: "Remove this heading (applies after Save).",
  },
  {
    headerName: "Column",
    field: "column",
    editable: false,
    pinned: "left",
    width: 230,
    cellStyle: { fontWeight: 500 },
    headerTooltip: "The merchant metric (Excel column) this rule applies to.",
  },
  {
    headerName: "Operator",
    field: "operator",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", "gte", "lte", "gt", "lt", "eq"] },
    width: 110,
    headerTooltip:
      "How the value is compared to the Threshold to flag a row / trigger a case: gte = at or above (≥), lte = at or below (≤), gt = above, lt = below, eq = exact match (used for text like ‘Exceed’). Leave blank for a display-only column that never triggers.",
  },
  {
    headerName: "Threshold",
    field: "threshold",
    width: 130,
    headerTooltip:
      "The value the metric is compared against using the Operator. A number for gte/lte/gt/lt, or text for eq (e.g. ‘Exceed’, ‘3 Months Decreasing’).",
  },
  {
    headerName: "Case freq",
    field: "thresholdForNextCase",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", "Weekly", "Daily"] },
    width: 115,
    headerTooltip:
      "How often a flagged value can open a NEW case: Weekly = at most once per Mon–Sun week; Daily = every day it triggers.",
  },
  {
    headerName: "Condition",
    field: "thresholdCondition",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", "higher", "lower"] },
    width: 115,
    headerTooltip:
      "When a case already exists this week, only open another if the new value is higher or lower than the previous one. Blank = open whenever it triggers (subject to Case freq).",
  },
  {
    headerName: "Range",
    field: "range",
    width: 90,
    headerTooltip:
      "Used only with Condition = higher: require the value to exceed the previous one by at least this amount before opening another case (ignores small increases).",
  },
  {
    headerName: "Color",
    field: "color",
    width: 110,
    cellEditor: "agSelectCellEditor",
    // 5 highlight colours; the default (red) is kept last. "" = no highlight.
    cellEditorParams: { values: ["", "orange", "yellow", "lightgreen", "lightblue", "red"] },
    cellStyle: (params) => ({ backgroundColor: params.value || "transparent" }),
    headerTooltip:
      "Highlight colour applied to the cell on the uploaded sheet when this rule triggers. Pick from orange, yellow, lightgreen, lightblue or red (the default). Leave blank for no highlight.",
  },
  {
    headerName: "Datatype",
    field: "datatype",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", "percentage", "currency"] },
    width: 120,
    headerTooltip:
      "How the value is formatted/stored: currency (rounded to a whole number) or percentage (kept as-is). Blank = plain number.",
  },
  {
    headerName: "Symbol",
    field: "symbol",
    width: 80,
    headerTooltip: "Currency symbol shown for currency values (e.g. $).",
  },
  {
    headerName: "Exact value",
    field: "isExactValueRequired",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", "Yes", "No"] },
    width: 110,
    headerTooltip:
      "Yes = preserve and display the exact value for this column instead of rounding. Used for precise rates like CB Rate / Refund Rate.",
  },
];

const defaultColDef = { editable: true, resizable: true, sortable: true, singleClickEdit: true, suppressMenu: true };

function RuleManagement(props) {
  const toast = useToast();
  const history = useHistory();
  const canManage = AuthService.hasPermission("manageRules");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const addModal = useDisclosure();
  const removeDialog = useDisclosure();
  const auditModal = useDisclosure();
  const gridRef = useRef();
  // The selected country comes from the URL (/preference/rule-management/:country),
  // so a refresh keeps you on the same country.
  const selected = props.country || DEFAULT_KEY;
  const [rowData, setRowData] = useState([]);
  const [customized, setCustomized] = useState([]);
  const [newHeading, setNewHeading] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState([]);
  const [countryList, setCountryList] = useState([DEFAULT_ENTRY]);

  const notify = (title, status) =>
    toast({ title, position: "top", status, isClosable: true });

  const loadCustomized = async () => {
    const res = await rulesService.getCustomizedCountries();
    if (res && res.customized) setCustomized(res.customized);
  };

  // Selectable countries come from the DB (so deleted countries disappear here too).
  const loadCountryList = async () => {
    const res = await countriesService.getCountries();
    if (res && res.countries) {
      setCountryList([DEFAULT_ENTRY, ...res.countries.map((c) => ({ code: c.code, name: c.name }))]);
    }
  };

  const loadCountry = async (code) => {
    props.setisLoading(true);
    const res = await rulesService.getCountryRules(code);
    props.setisLoading(false);
    if (res && res.rules) {
      setRowData(rulesToRows(res.rules));
    } else {
      notify((res && (res.warning || res.error)) || "Failed to load rules", "error");
    }
  };

  useEffect(() => {
    loadCustomized();
    loadCountryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload whenever the country in the URL changes (initial load + left-list clicks).
  useEffect(() => {
    loadCountry(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const readRows = () => {
    const rows = [];
    gridRef.current.api.forEachNode((node) => rows.push(node.data));
    return rows;
  };

  // Stretch the columns to fill the whole grid width (on load and on resize).
  const fitColumns = (params) => params.api.sizeColumnsToFit();

  const emptyRow = (column) => ({
    column,
    operator: "",
    threshold: "",
    thresholdForNextCase: "",
    thresholdCondition: "",
    range: "",
    color: "",
    datatype: "",
    symbol: "",
    isExactValueRequired: "",
  });

  const onAddHeading = () => {
    const name = newHeading.trim();
    if (!name) {
      notify("Enter a heading name", "warning");
      return;
    }
    const rows = readRows();
    if (rows.some((r) => (r.column || "").toLowerCase() === name.toLowerCase())) {
      notify("That heading already exists", "warning");
      return;
    }
    setRowData([...rows, emptyRow(name)]);
    setNewHeading("");
    addModal.onClose();
    notify(`Added "${name}" — set its rule and Save`, "info");
  };

  // open the confirm dialog for a single heading (per-row trash icon)
  const onRemoveRow = (column) => {
    setPendingRemoval([column]);
    removeDialog.onOpen();
  };

  const performRemove = () => {
    const names = new Set(pendingRemoval);
    setRowData(readRows().filter((r) => !names.has(r.column)));
    notify(`Removed ${pendingRemoval.length} heading(s) — Save to persist`, "info");
    setPendingRemoval([]);
  };

  const onSave = async () => {
    if (gridRef.current) gridRef.current.api.stopEditing();
    const rules = rowsToRules(readRows());
    props.setisLoading(true);
    const res = await rulesService.updateCountryRules(selected, rules);
    props.setisLoading(false);
    if (res && res.rules) {
      notify(`Saved rules for ${labelFor(selected)}`, "success");
      loadCustomized();
    } else {
      notify((res && (res.warning || res.error)) || "Save failed", "error");
    }
  };

  const onReset = async () => {
    props.setisLoading(true);
    const res = await rulesService.resetCountryRules(selected);
    props.setisLoading(false);
    if (res && res.rules) {
      setRowData(rulesToRows(res.rules));
      notify(`Reset ${labelFor(selected)} to default`, "success");
      loadCustomized();
    } else {
      notify((res && (res.warning || res.error)) || "Reset failed", "error");
    }
  };

  const labelFor = (code) => (countryList.find((c) => c.code === code) || {}).name || code;

  return (
    <Box w="100%">
      <Flex alignItems="center" mb={2}>
        <Button size="xs" leftIcon={<AiOutlineArrowLeft />} variant="ghost" onClick={props.onBack}>
          Back to settings
        </Button>
        <Spacer />
        <Heading size="sm">Rule Management</Heading>
        <Spacer />
      </Flex>

      <Flex>
        {/* Left: country list */}
        <Box w="200px" mr={3} borderWidth="1px" borderRadius="md" p={1}>
          {countryList.map((c) => {
            const isSel = c.code === selected;
            return (
              <Flex
                key={c.code}
                p={2}
                mb={1}
                borderRadius="md"
                cursor="pointer"
                alignItems="center"
                backgroundColor={isSel ? "teal.500" : "transparent"}
                color={isSel ? "white" : "inherit"}
                _hover={{ backgroundColor: isSel ? "teal.500" : "gray.100" }}
                onClick={() => history.push(`/preference/rule-management/${c.code}`)}
              >
                <Text fontSize="13px">{c.name}</Text>
                <Spacer />
                {c.code !== DEFAULT_KEY && customized.includes(c.code) && (
                  <Badge colorScheme={isSel ? "whiteAlpha" : "purple"} fontSize="0.6em">
                    custom
                  </Badge>
                )}
              </Flex>
            );
          })}
        </Box>

        {/* Right: editable rule grid */}
        <Box flex="1">
          <Flex mb={2} alignItems="center">
            <Text fontSize="14px" fontWeight="500">
              {labelFor(selected)} rules
            </Text>
            <Spacer />
            {canManage && (
              <Button size="xs" mr={2} leftIcon={<AiOutlinePlus />} variant="outline" colorScheme="teal" onClick={() => addModal.onOpen()}>
                Add heading
              </Button>
            )}
            <Button size="xs" mr={2} leftIcon={<AiOutlineHistory />} variant="ghost" onClick={() => auditModal.onOpen()}>
              History
            </Button>
            {canManage && (
              <Button size="xs" mr={2} colorScheme="teal" onClick={onSave}>
                Save
              </Button>
            )}
            {canManage && (
              <Button size="xs" variant="outline" colorScheme="red" onClick={onOpen}>
                Reset to default
              </Button>
            )}
          </Flex>
          <Box className="ag-theme-alpine" h="70vh" w="100%">
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={canManage ? columnDefs : columnDefs.filter((c) => c.field !== "__remove")}
              defaultColDef={{ ...defaultColDef, editable: canManage }}
              frameworkComponents={{
                removeBtn: (params) => (
                  <IconButton
                    aria-label="Remove heading"
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    icon={<AiOutlineDelete />}
                    onClick={() => onRemoveRow(params.data.column)}
                  />
                ),
              }}
              stopEditingWhenCellsLoseFocus={true}
              animateRows={true}
              enableBrowserTooltips={true}
              onGridReady={fitColumns}
              onFirstDataRendered={fitColumns}
            />
          </Box>
        </Box>
      </Flex>

      <ConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onReset={onReset}
        header="Reset rules"
        body={
          selected === DEFAULT_KEY
            ? "Reset the Default rules back to the built-in values? This affects every country without its own override."
            : `Reset ${labelFor(selected)} to the Default rules? Its custom overrides will be removed.`
        }
      />

      <RuleAuditModal
        isOpen={auditModal.isOpen}
        onClose={auditModal.onClose}
        country={selected}
        label={labelFor(selected)}
      />

      <ConfirmDialog
        isOpen={removeDialog.isOpen}
        onClose={removeDialog.onClose}
        onReset={performRemove}
        header="Remove heading"
        body={`Remove ${pendingRemoval.length} heading(s)${
          pendingRemoval.length ? ` (${pendingRemoval.join(", ")})` : ""
        }? This takes effect for ${labelFor(selected)} only after you click Save.`}
      />

      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Add a rule heading</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">Heading (Excel column name)</FormLabel>
              <Input
                value={newHeading}
                onChange={(e) => setNewHeading(e.target.value)}
                placeholder="e.g. Refund Velocity"
                onKeyDown={(e) => {
                  if (e.key === "Enter") onAddHeading();
                }}
                autoFocus
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                Use the exact column name as it appears in the uploaded sheet. It's added to{" "}
                <b>{labelFor(selected)}</b> — set its rule and click Save to persist.
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" mr={2} variant="ghost" onClick={addModal.onClose}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="teal" onClick={onAddHeading}>
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default RuleManagement;
